import React, { useState } from 'react';
import ProductCategoryTable from '../components/productCategory/ProductCategoryTable';
import ProductCategoryForm from '../components/productCategory/ProductCategoryForm';
import ProductCategoryImportModal from '../components/productCategory/ProductCategoryImportModal';
import ProductCategoryActions from '../components/productCategory/ProductCategoryActions';
import MessageAlert from '../components/common/MessageAlert';
import ConfirmDialog from '../components/common/ConfirmDialog';
import useProductCategory from '../hooks/useProductCategory';
import { AdminGuard } from '../components/RoleGuards';
import { useErrorDialog } from '../context/errorDialogContext';
import { useTranslation } from 'react-i18next';

/**
 * ProductCategoryPage Component
 * - Provides UI to list, create, edit, view, delete, bulk-delete, import, export 
 *   for product categories. Uses custom hook for data operations.
 */
const ProductCategoryPage = () => {
  const { t } = useTranslation();
  // Custom hook for product category data and operations
  const {
    // Data
    categories,
    totalRecords,
    currentPage,
    totalPages,
    totalCount,
    handlePageChange,

    // State
    loading,
    error,
    clearError,
    message,
    searchTerm,

    // Selection
    selectedCategories,
    hasSelectedCategories,
    selectedCount,

    // Modals and forms
    isFormOpen,
    isImportModalOpen,
    editingCategory,

    // Actions
    refreshData,

    // Search and filters
    handleSearch,

    // Selection
    handleSelectCategory,
    handleSelectAll,

    // Forms and modals
    openAddForm,
    openEditForm,
    closeForm,
    openImportModal,
    closeImportModal,

    // CRUD operations
    createCategory,
    updateCategory,
    deleteCategory,
    bulkDeleteCategories,

    // Import/Export
    importCategories,
    exportCategories,
    downloadSampleTemplate,
  } = useProductCategory();

  // Local state for UI interactions
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  const strings = t('productCategories', { returnObjects: true });
  const { showError } = useErrorDialog();

  /**
   * Global error watcher for category operations
   * - Displays errors via the global error dialog when `error` is set
   */
  React.useEffect(() => {
    if (error) {
      showError(error, strings.page?.alerts?.errorTitle);
      // Clear once shown: `error` sticks until the next request, and this effect re-runs on
      // every render because `strings.page` is a fresh object each time (t(..., { returnObjects:
      // true }) does not memoize). Leaving it set re-opens the dialog the operator just
      // dismissed, which reads as a frozen screen.
      clearError();
    }
  }, [error, showError, clearError, strings.page]);

  /** Refresh the categories list, forcing a server reload */
  const handleRefresh = async () => {
    setRefreshLoading(true);
    try {
      await refreshData();
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setRefreshLoading(false);
    }
  };

  /** Open category form for creating a new category */
  const handleCreateCategory = () => {
    openAddForm();
  };

  /** Open category form prefilled for editing the given category */
  const handleEditCategory = (category) => {
    openEditForm(category);
  };


  /**
   * Prepare delete confirmation for a single category
   * - resolves display name for the confirm message and sets confirm state
   */
  const handleDeleteCategory = (categoryOrId) => {
    // Handle both category object and category ID
    const categoryId = typeof categoryOrId === 'object' ? categoryOrId?.id : categoryOrId;
    const category = categories.find((c) => c.id === categoryId) || null;
    const displayName = category?.name || strings.table?.copy?.unnamedCategory || t('productCategories.table.copy.unnamedCategory');

    setDeleteConfirm({
      type: 'single',
      category,
      categoryId: categoryId, // Ensure we store the actual ID
      title: strings.confirmDialog?.deleteTitle,
      message: (strings.confirmDialog?.deleteMessage).replace('{name}', displayName),
      confirmLabel: strings.confirmDialog?.confirmLabel,
      loadingLabel: strings.confirmDialog?.loadingLabel,
      cancelLabel: strings.confirmDialog?.buttons?.cancel,
      confirmVariant: 'danger',
    });
  };

  /**
   * Prepare delete confirmation for bulk selected categories
   */
  const handleBulkDeleteCategories = () => {
    // If exactly one is selected, try to show the single-item message with the category name
    let message;
    if (selectedCount === 1) {
      const singleId = Array.isArray(selectedCategories) ? selectedCategories[0] : null;
      const singleCategory = categories.find((c) => c.id === singleId) || null;
      const displayName = singleCategory?.name || strings.table?.copy?.unnamedCategory;
      message = (strings.confirmDialog?.deleteMessage).replace('{name}', displayName);
    } else {
      message = (strings.confirmDialog?.bulkDeleteMessage).replace('{count}', selectedCount);
    }

    setDeleteConfirm({
      type: 'bulk',
      count: selectedCount,
      title: strings.confirmDialog?.bulkDeleteTitle,
      message,
      confirmLabel: strings.confirmDialog?.confirmLabel,
      loadingLabel: strings.confirmDialog?.loadingLabel,
      cancelLabel: strings.confirmDialog?.buttons?.cancel,
      confirmVariant: 'danger',
    });
  };

  /** Execute the confirmed delete (single or bulk) operation */
  const confirmDelete = async () => {
    setActionLoading(true);
    try {
      let result;

      if (deleteConfirm.type === 'single') {
        const idToDelete = deleteConfirm.categoryId || deleteConfirm.category?.id;
        if (!idToDelete) {
          const msg = strings.errors?.noIdToDelete || t('productCategories.errors.noIdToDelete');
          throw new Error(msg);
        }
        // Ensure we pass only the numeric ID, not an object
        const numericId = typeof idToDelete === 'object' ? idToDelete.id : idToDelete;
        result = await deleteCategory(numericId);
      } else {
        // For bulk delete ensure we pass the selected category IDs as an array of numbers
        const categoryIds = Array.isArray(selectedCategories)
          ? selectedCategories.map(id => typeof id === 'object' ? id.id : id)
          : [];
        result = await bulkDeleteCategories(categoryIds);
      }

      if (result.success) {
        setDeleteConfirm(null);
      } else {
        // Service returned an error - close dialog and show error message
        const errorMsg = result.error;
        setDeleteConfirm(null);
        setAlertMessage({
          type: 'error',
          title: strings.confirmDialog?.errorTitle || t('common.dialogs.confirm.title'),
          message: errorMsg
        });
      }
    } catch (err) {
      console.error('Delete operation failed:', err);
      // Exception occurred - close dialog and show error message
      const errorMsg = err?.message;
      setDeleteConfirm(null);
      setAlertMessage({
        type: 'error',
        title: strings.confirmDialog?.errorTitle || t('common.dialogs.confirm.title'),
        message: errorMsg
      });
    } finally {
      setActionLoading(false);
    }
  };

  /** Handle submit from the category form for create or update */
  const handleFormSubmit = async (categoryData) => {
    setActionLoading(true);
    try {
      let result;

      if (editingCategory) {
        result = await updateCategory(editingCategory.id, categoryData);
      } else {
        result = await createCategory(categoryData);
      }

      if (result.success) {
        closeForm();
      }

      return result;
    } catch (err) {
      console.error('Form submission failed:', err);
      const fallback = editingCategory ? (strings.errors?.updateFailed || t('productCategories.errors.updateFailed')) : (strings.errors?.createFailed || t('productCategories.errors.createFailed'));
      return { success: false, error: fallback };
    } finally {
      setActionLoading(false);
    }
  };

  /** Handle category import */
  const handleCategoryImport = async (file) => {
    const result = await importCategories(file);
    if (result.success) {
      closeImportModal();
    }
    return result;
  };

  /** Export categories */
  const handleCategoryExport = async (exportFilters = {}) => {
    setActionLoading(true);
    try {
      const result = await exportCategories(exportFilters);
      return result;
    } catch (err) {
      console.error('Export failed:', err);
      return { success: false, error: t('productCategories.errors.exportFailed') };
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Count categories helper
   * - Prefer the server-provided totalRecords when available, otherwise fall back
   *   to the length of the current `categories` array. Returns a number.
   */
  const countCategories = () => {
    if (typeof totalRecords === 'number' && totalRecords >= 0) return totalRecords;
    if (Array.isArray(categories)) return categories.length;
    return 0;
  };

  // Value used by the header stat UI
  const totalCategories = countCategories();

  return (
    <AdminGuard fallback={(
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold">{strings.page?.accessDenied?.title || 'Access Denied'}</h2>
          <p className="text-gray-600">{strings.page?.accessDenied?.message}</p>
        </div>
      </div>
    )}>
      <div className="min-h-screen bg-gray-50 p-6">
        {/* Alert Message */}
        {alertMessage && (
          <div className="fixed top-4 right-4 z-50">
            <MessageAlert
              type={alertMessage.type}
              title={alertMessage.title}
              message={alertMessage.message}
              onClose={() => setAlertMessage(null)}
            />
          </div>
        )}

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{strings.page?.header?.title || strings.title}</h1>
              <p className="mt-2 text-gray-600">
                {strings.page?.header?.subtitle || strings.description}
              </p>
            </div>

            {/* Stats */}
            <div className="flex space-x-4">
              <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-gray-500">{strings.page?.stats?.totalCategories}</div>
                  <div className="text-2xl font-bold text-gray-900">{totalCategories}</div>
                </div>
              </div>
              {hasSelectedCategories && (
                <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                  <div className="flex items-center space-x-3">
                    <div className="text-sm text-blue-600">{strings.page?.stats?.selected}</div>
                    <div className="text-2xl font-bold text-blue-700">{selectedCount}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <ProductCategoryActions
          selectedCategories={selectedCategories}
          categories={categories}
          onAddCategory={handleCreateCategory}
          onDeleteSelected={handleBulkDeleteCategories}
          onRefresh={handleRefresh}
          onImport={openImportModal}
          onExport={handleCategoryExport}
          onDownloadTemplate={downloadSampleTemplate}
          searchValue={searchTerm}
          onSearchChange={handleSearch}
          loading={actionLoading}
          refreshLoading={refreshLoading}
          importing={loading}
          exporting={actionLoading}
        />

        {/* Categories Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6">
          <ProductCategoryTable
            categories={categories}
            loading={loading}
            selectedCategories={selectedCategories}
            onSelectCategory={handleSelectCategory}
            onSelectAll={handleSelectAll}
            onEdit={handleEditCategory}
            onDelete={handleDeleteCategory}
          />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            className="mt-4 flex items-center justify-between rounded-xl border px-6 py-3"
            style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}
          >
            <span className="text-sm" style={{ color: '#6b7280' }}>
              Page {currentPage} of {totalPages} — {totalCount} total categories
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1 || loading}
                className="rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6b7280' }}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p);
                  acc.push(p);
                  return acc;
                }, [])
                .map((item) =>
                  typeof item === 'string' ? (
                    <span key={item} className="px-2 text-sm" style={{ color: '#6b7280' }}>…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => handlePageChange(item)}
                      disabled={loading}
                      className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: item === currentPage ? '#0066cc' : 'transparent',
                        color: item === currentPage ? '#fff' : '#374151',
                        border: item === currentPage ? 'none' : '1px solid rgba(0,0,0,0.15)',
                      }}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages || loading}
                className="rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: '#0066cc', color: '#fff' }}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Category Form Modal */}
        {isFormOpen && (
          <ProductCategoryForm
            category={editingCategory}
            isOpen={isFormOpen}
            onClose={closeForm}
            onSubmit={handleFormSubmit}
            loading={actionLoading}
          />
        )}

        {/* Import Modal */}
        {isImportModalOpen && (
          <ProductCategoryImportModal
            isOpen={isImportModalOpen}
            onClose={closeImportModal}
            onImport={handleCategoryImport}
            importing={loading}
            importError={error}
            importSuccess={message}
          />
        )}

        {/* Delete Confirmation Dialog */}
        {deleteConfirm && (
          <ConfirmDialog
            isOpen
            title={deleteConfirm.title}
            message={deleteConfirm.message}
            confirmLabel={deleteConfirm.confirmLabel}
            cancelLabel={deleteConfirm.cancelLabel}
            confirmVariant={deleteConfirm.confirmVariant}
            loading={actionLoading}
            loadingLabel={deleteConfirm.loadingLabel}
            onConfirm={confirmDelete}
            onCancel={() => setDeleteConfirm(null)}
          />
        )}
      </div>
    </AdminGuard>
  );
};

export default ProductCategoryPage;